/**
 * Header Notifications Component
 * Displays notifications in a popover triggered from the header
 */

'use client';

import * as PopoverPrimitive from '@radix-ui/react-popover';
import { AlertTriangle, Bell, CheckCircle2, EyeOffIcon, Info } from 'lucide-react';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type {
  DrupalNotification,
  HeaderNotificationsProps,
  NotificationTokens,
  NotificationVariant,
} from '../types';
import {
  applyTokens,
  calculateBadgeClass,
  filterUnseenNotifications,
  getNotificationHtml,
  getNotificationTitle,
  getNotificationVariant,
} from '../utils';
import { markNotificationAsRead } from '../api';
import type { UseDrupalCollectionHook } from '../hooks';
import { useNotificationsForMembership, useMembershipTypeId, useNotificationsByApplication } from '../hooks';

/**
 * UI component dependencies that must be provided by the consuming app
 */
export interface HeaderNotificationsUIProps {
  /** Button component for the trigger and actions */
  Button: React.ComponentType<{
    variant?: 'ghost' | 'default' | 'outline' | 'secondary' | 'link' | 'destructive';
    size?: 'default' | 'sm' | 'lg' | 'icon';
    className?: string;
    disabled?: boolean;
    onClick?: () => void;
    children?: React.ReactNode;
    title?: string;
    'aria-label'?: string;
    asChild?: boolean;
  }>;
  /** Prose component for rendering HTML content */
  Prose?: React.ComponentType<{
    content: string;
    className?: string;
    maxLength?: number;
  }>;
}

/**
 * Complete props for HeaderNotifications including UI dependencies
 */
export interface HeaderNotificationsFullProps extends HeaderNotificationsProps {
  /** UI component dependencies */
  ui: HeaderNotificationsUIProps;
  /** Drupal collection hook */
  useDrupalCollection: UseDrupalCollectionHook;
  /**
   * Application name(s) for simple filtering (recommended for single-app use)
   * Uses field_app_name to filter notifications by application
   * This disables membership/assembly filtering for a cleaner, simpler mode
   */
  applicationName?: string | string[];
  /** Membership taxonomy name for filtering (ignored if applicationId is set) */
  membershipTaxonomyName?: string | null;
  /** Assembly/group IDs for filtering (ignored if applicationId is set) */
  assemblyIds?: string[];
  /** Application IDs for filtering (ignored if applicationId is set) */
  applicationIds?: string[];
  /** Token replacement variables */
  tokens?: NotificationTokens;
}

/**
 * Get icon component for notification variant
 */
function getVariantIcon(variant: NotificationVariant): React.ReactNode {
  switch (variant) {
    case 'alert':
      return <AlertTriangle className="h-4 w-4 shrink-0 text-red-600" />;
    case 'warning':
      return <AlertTriangle className="h-4 w-4 shrink-0 text-amber-500" />;
    case 'success':
      return <CheckCircle2 className="h-4 w-4 shrink-0 text-emerald-600" />;
    case 'info':
    default:
      return <Info className="h-4 w-4 shrink-0 text-blue-600" />;
  }
}

/**
 * Header Notifications Component
 * Displays user notifications in a popover with mark-as-read functionality
 */
export function HeaderNotifications({
  userEmail,
  userContext,
  maxNotifications = 5,
  ui,
  useDrupalCollection,
  applicationName,
  membershipTaxonomyName,
  assemblyIds = [],
  applicationIds = [],
  tokens = {},
}: HeaderNotificationsFullProps) {
  const { Button, Prose } = ui;
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [hidingIds, setHidingIds] = useState<Set<string>>(new Set());
  const [hiddenIds, setHiddenIds] = useState<Set<string>>(new Set());

  // Simple mode: only filter by applicationName
  const appNames = Array.isArray(applicationName) ? applicationName : applicationName ? [applicationName] : [];
  const useSimpleMode = appNames.length > 0;

  // Get membership type ID (only needed in complex mode)
  const { id: membershipTypeId } = useMembershipTypeId({
    membershipTaxonomyName: useSimpleMode ? null : membershipTaxonomyName,
    useDrupalCollection,
  });

  // Fetch notifications - simple mode (by application name only)
  const simpleResult = useNotificationsByApplication({
    applicationName: applicationName || '',
    maxNotifications,
    useDrupalCollection,
  });

  // Fetch notifications - complex mode (membership + assembly + applications)
  const complexResult = useNotificationsForMembership({
    membershipTypeId: useSimpleMode ? null : membershipTypeId,
    assemblyIds: useSimpleMode ? [] : assemblyIds,
    applicationIds: useSimpleMode ? [] : applicationIds,
    maxNotifications,
    includePublic: !useSimpleMode,
    useDrupalCollection,
  });

  // Use appropriate result based on mode
  const { items, isLoading, error } = useSimpleMode ? simpleResult : complexResult;

  // Filter notifications
  const displayItems = useMemo(
    () => filterUnseenNotifications(items, userEmail ?? null, hiddenIds),
    [items, userEmail, hiddenIds]
  );

  const count = displayItems.length;
  const disabled = !isLoading && !error && count === 0;

  // Close popover when no notifications
  useEffect(() => {
    if (open && count === 0) setOpen(false);
  }, [open, count]);

  // Calculate badge class
  const badgeClass = useMemo(() => calculateBadgeClass(displayItems), [displayItems]);

  // Mutation for marking as read
  const mutation = useMutation({
    mutationFn: async (vars: { id: string }) => {
      if (!userEmail) throw new Error('User email is required');
      await markNotificationAsRead(vars.id, userEmail);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['drupal-collection', 'node--notifications'] });
    },
  });

  const handleHide = useCallback(
    (id: string) => {
      setHidingIds(prev => new Set(prev).add(id));
      window.setTimeout(() => {
        setHiddenIds(prev => new Set(prev).add(id));
        mutation.mutate({ id });
      }, 200);
    },
    [mutation]
  );

  return (
    <PopoverPrimitive.Root open={open} onOpenChange={setOpen}>
      <PopoverPrimitive.Trigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="relative"
          aria-label="Notifications"
          disabled={disabled}
        >
          <Bell className="h-5 w-5" />
          {count > 0 ? (
            <span
              className={`absolute text-black -right-1 -top-1 inline-flex h-4 min-w-4 items-center justify-center rounded-full px-1 text-[10px] font-medium ${badgeClass}`}
            >
              {count}
            </span>
          ) : null}
        </Button>
      </PopoverPrimitive.Trigger>
      <PopoverPrimitive.Portal>
        <PopoverPrimitive.Content align="end" className="w-80 rounded-md border bg-popover p-0 text-popover-foreground shadow-md outline-none z-50">
          <div className="border-b px-3 py-2 text-sm font-medium">Notifications</div>
          <div className="max-h-96 overflow-auto">
            {isLoading ? (
              <div className="space-y-2 p-3">
                <div className="h-4 w-40 animate-pulse rounded bg-muted" />
                <div className="h-4 w-52 animate-pulse rounded bg-muted" />
              </div>
            ) : error ? (
              <div className="p-3 text-xs text-red-600">Failed to load notifications.</div>
            ) : displayItems.length === 0 ? (
              <div className="p-3 text-xs text-muted-foreground">No notifications.</div>
            ) : (
              <ul className="space-y-2">
                {displayItems.map((notification, idx) => {
                  const title = getNotificationTitle(notification);
                  const id = String(notification.id || '');
                  const variant = getNotificationVariant(notification);
                  const tokenRecord: Record<string, string> = Object.fromEntries(
                    Object.entries(tokens).filter(([, v]) => v !== undefined) as Array<[string, string]>
                  );
                  const htmlContent = applyTokens(getNotificationHtml(notification), tokenRecord);
                  const icon = getVariantIcon(variant);
                  const isHiding = hidingIds.has(id);

                  return (
                    <li
                      key={`${title}-${idx}`}
                      className={`px-3 mt-2 border-b transition-all duration-200 space-y-2 ${
                        isHiding ? 'opacity-0 translate-y-1 scale-95' : ''
                      }`}
                    >
                      <div className="flex items-start gap-2">
                        {icon}
                        <div className="text-sm font-semibold leading-tight tracking-tight flex-1">
                          {title}
                        </div>
                      </div>
                      {htmlContent && Prose ? (
                        <Prose content={htmlContent} className="text-xs px-2 opacity-90" maxLength={1000} />
                      ) : htmlContent ? (
                        <div
                          className="text-xs px-2 opacity-90 prose prose-sm dark:prose-invert"
                          dangerouslySetInnerHTML={{ __html: htmlContent }}
                        />
                      ) : null}
                      <div className="flex justify-end">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleHide(id)}
                          className="cursor-pointer hover:text-red-600"
                          title="Hide this notification"
                        >
                          <EyeOffIcon className="size-4" />{' '}
                          <span className="text-xs sr-only">Hide</span>
                        </Button>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </PopoverPrimitive.Content>
      </PopoverPrimitive.Portal>
    </PopoverPrimitive.Root>
  );
}

